import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, CheckCircle2, Loader2, Plug, Unplug, AlertTriangle, RefreshCw, Database, ShieldCheck, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hs, setHs] = useState(null);
  const [hsBusy, setHsBusy] = useState("");
  const [allow, setAllow] = useState(null);
  const [newDomain, setNewDomain] = useState("");
  const [alBusy, setAlBusy] = useState("");

  const canUse = !!user;  // Dir 65 — roles flattened: any authenticated employee can use settings/integrations.

  const load = () => api.get("/oauth/gmail/status").then((r) => setStatus(r.data)).catch(() => {});
  const loadHs = () => api.get("/integrations/hubspot/status").then((r) => setHs(r.data)).catch(() => {});
  const loadAllow = () => api.get("/settings/allowlist").then((r) => setAllow(r.data)).catch(() => {});

  useEffect(() => {
    if (!canUse) return;
    load();
    loadHs();
    loadAllow();
    const g = params.get("gmail");
    if (g === "connected") { toast.success("Gmail connected"); checkAuth?.(); }
    if (g === "error") toast.error("Gmail connection failed. Please try again.");
    if (g) { params.delete("gmail"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const testHubspot = async () => {
    setHsBusy("test");
    try {
      const r = await api.post("/integrations/hubspot/test");
      if (r.data.ok) toast.success(`HubSpot connected — pipeline ${r.data.pipeline_id}.`);
      else toast.error(r.data.error || "HubSpot test failed.");
      setHs((h) => ({ ...(h || {}), ...r.data }));
    } catch (e) { toast.error(e?.response?.data?.detail || "HubSpot test failed."); }
    finally { setHsBusy(""); }
  };

  const syncAllHubspot = async () => {
    setHsBusy("sync");
    try {
      const r = await api.post("/integrations/hubspot/sync-all");
      toast.success(`Queued ${r.data.queued} deal(s) to HubSpot.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed."); }
    finally { setHsBusy(""); }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/oauth/gmail/login");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Gmail connection");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Gmail? You won't be able to submit bids from your account until you reconnect.")) return;
    setBusy(true);
    try {
      await api.post("/oauth/gmail/disconnect");
      toast.success("Gmail disconnected");
      await load();
      checkAuth?.();
    } catch { toast.error("Failed to disconnect"); }
    finally { setBusy(false); }
  };

  // Dir 147.2 — client-side allowlist validation mirrors url_ingest.normalize_domain (server re-validates).
  const validateDomain = (raw) => {
    const s = (raw || "").trim().toLowerCase().replace(/\.+$/, "");
    if (!s) return "Enter a domain.";
    if (/[*/\\:\s@?#]/.test(s)) return "No wildcards, paths, ports, credentials, or spaces — enter a bare domain like agency.gov";
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(s) || s.includes("::")) return "IP addresses are not allowed — enter a domain name.";
    const labels = s.split(".");
    if (labels.length < 2 || labels.some((l) => !l)) return "Enter a full registrable domain (e.g. agency.gov), not a bare TLD.";
    if (!/^[a-z]{2,}$/.test(labels[labels.length - 1])) return "Invalid top-level domain.";
    return null;
  };

  const addDomain = async () => {
    const err = validateDomain(newDomain);
    if (err) { toast.error(err); return; }
    setAlBusy("add");
    try {
      const r = await api.post("/settings/allowlist", { domain: newDomain.trim() });
      toast.success(`Added ${r.data.domain} to the allowlist.`);
      setNewDomain("");
      setAllow((a) => ({ ...(a || {}), dynamic: r.data.dynamic }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add domain."); }
    finally { setAlBusy(""); }
  };

  const removeDomain = async (dom) => {
    if (!window.confirm(`Remove ${dom} from the URL-ingest allowlist?`)) return;
    setAlBusy(dom);
    try {
      const r = await api.delete(`/settings/allowlist/${encodeURIComponent(dom)}`);
      toast.success(`Removed ${dom}.`);
      setAllow((a) => ({ ...(a || {}), dynamic: r.data.dynamic }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove domain."); }
    finally { setAlBusy(""); }
  };

  if (!canUse) {
    return <div className="max-w-2xl mx-auto p-8 text-sm text-zinc-500" data-testid="settings-denied">
      Please sign in to manage your integrations.
    </div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-6 md:p-8" data-testid="settings-page">
      <h1 className="text-2xl font-display font-bold text-zinc-900 dark:text-white mb-1">Settings</h1>
      <p className="text-sm text-zinc-500 mb-6">Connect your accounts to submit bids directly.</p>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 shadow-sm" data-testid="gmail-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
            <Mail className="h-5 w-5 text-zinc-700 dark:text-zinc-200" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">Gmail — bid submission</h2>
              {status?.connected && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Connected</span>}
            </div>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              Send the bid submission email (with the proposal PDF attached) straight from your own Gmail.
              You review and edit before it sends; the email is logged on the deal.
            </p>

            {status && !status.configured && (
              <div className="mt-3 text-xs text-amber-600 flex items-center gap-1.5" data-testid="gmail-not-configured">
                <AlertTriangle className="h-3.5 w-3.5" /> Gmail is not configured on the server yet.
              </div>
            )}

            {status?.connected ? (
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-zinc-600 dark:text-zinc-300" data-testid="gmail-connected-email">
                  Connected as <span className="font-mono">{status.email}</span>
                </div>
                <button onClick={disconnect} disabled={busy} data-testid="gmail-disconnect-btn"
                  className="flex items-center gap-1.5 text-xs border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 px-3 py-1.5 rounded-md hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />} Disconnect
                </button>
              </div>
            ) : (
              <button onClick={connect} disabled={busy || (status && !status.configured)} data-testid="gmail-connect-btn"
                className="mt-4 flex items-center gap-2 text-xs bg-zinc-900 text-white px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Connect Gmail
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 shadow-sm mt-5" data-testid="hubspot-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
            <Database className="h-5 w-5 text-zinc-700 dark:text-zinc-200" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">HubSpot — deal mirror</h2>
              {hs?.configured
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Configured</span>
                : <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Not configured</span>}
            </div>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              One-way sync: Ironclad mirrors every deal into HubSpot and keeps its stage in step as it moves.
              HubSpot never gates Ironclad — if the sync fails, your bid workflow keeps going and we log the error.
            </p>

            {hs && !hs.configured && (
              <div className="mt-3 text-xs text-amber-600 flex items-center gap-1.5" data-testid="hubspot-not-configured">
                <AlertTriangle className="h-3.5 w-3.5" /> Add a HubSpot Private App token (HUBSPOT_ACCESS_TOKEN) on the server to enable.
              </div>
            )}

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <button onClick={testHubspot} disabled={!!hsBusy || !hs?.configured} data-testid="hubspot-test-btn"
                className="flex items-center gap-2 text-xs bg-zinc-900 text-white px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-50">
                {hsBusy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Test connection
              </button>
              <button onClick={syncAllHubspot} disabled={!!hsBusy || !hs?.configured} data-testid="hubspot-syncall-btn"
                className="flex items-center gap-2 text-xs border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 px-4 py-2 rounded-md hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50">
                {hsBusy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync all deals
              </button>
            </div>
            {hs?.pipeline_id && (
              <div className="mt-3 text-[11px] font-mono text-zinc-500" data-testid="hubspot-pipeline">
                Pipeline: {hs.pipeline_id}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5 shadow-sm mt-5" data-testid="allowlist-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-zinc-700 dark:text-zinc-200" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">URL-ingest allowlist</h2>
              {allow?.is_admin
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="allowlist-admin-badge">Admin</span>
                : <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500" data-testid="allowlist-readonly-badge">Read-only</span>}
            </div>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              The only domains Ironclad will fetch a solicitation from when you paste a URL. Every fetch is
              SSRF-hardened (public-IP-pinned, redirects re-validated each hop, 25&nbsp;MB cap).{" "}
              {allow?.is_admin ? "Add a public procurement portal below." : "Only an admin can change this list."}
            </p>

            {allow?.is_admin && (
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !alBusy && addDomain()}
                  placeholder="agency.gov"
                  data-testid="allowlist-add-input"
                  disabled={!!alBusy}
                  className="flex-1 text-xs px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400" />
                <button onClick={addDomain} disabled={!!alBusy || !newDomain.trim()} data-testid="allowlist-add-btn"
                  className="flex items-center gap-1.5 text-xs bg-zinc-900 text-white px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-50">
                  {alBusy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
                </button>
              </div>
            )}

            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1.5">Added domains</div>
              {(allow?.dynamic || []).length === 0 ? (
                <div className="text-xs text-zinc-400 italic" data-testid="allowlist-empty">No admin-added domains yet.</div>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="allowlist-dynamic">
                  {(allow?.dynamic || []).map((d) => (
                    <span key={d} data-testid={`allowlist-domain-${d}`}
                      className="flex items-center gap-1.5 text-xs font-mono bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 pl-2.5 pr-1.5 py-1 rounded-full">
                      {d}
                      {allow?.is_admin && (
                        <button onClick={() => removeDomain(d)} disabled={!!alBusy} data-testid={`allowlist-remove-${d}`}
                          className="rounded-full hover:bg-zinc-300 dark:hover:bg-zinc-700 p-0.5 disabled:opacity-50" title={`Remove ${d}`}>
                          {alBusy === d ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1.5">Built-in (read-only)</div>
              <div className="flex flex-wrap gap-1.5" data-testid="allowlist-seed">
                {(allow?.seed || []).map((d) => (
                  <span key={d} className="text-[11px] font-mono text-zinc-500 bg-zinc-50 dark:bg-zinc-800/50 px-2 py-0.5 rounded">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
